import React from "react";
import "./PostDetailModal.css";

function renderLinkedContent(text) {
  if (!text) return null;

  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = String(text).split(urlRegex);

  return parts.map((part, index) => {
    if (!/^https?:\/\//i.test(part)) {
      return <React.Fragment key={`text-${index}`}>{part}</React.Fragment>;
    }

    const isYouTube = /youtube\.com|youtu\.be/i.test(part);

    // YouTube stays inside the post because the modal renders the video below.
    if (isYouTube) {
      return <React.Fragment key={`youtube-${index}`}>{part}</React.Fragment>;
    }

    return (
      <a
        key={`link-${index}`}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
      >
        {part}
      </a>
    );
  });
}

function PostDetailModal({
  post,
  onClose,
  onReply,
  onLike,
  onRepost,
  onNavigateToProfile,
}) {
  // 🔒 Safety: if no post, don't render anything
  if (!post) return null;

  // ⭐ Normalize counts so this matches Home/Profile behaviour
  const normalizedPost = {
    ...post,

    // ❤️ Likes
    likes: post.likes ?? post.like_count ?? 0,

    // 💬 Replies
    repliesCount:
      post.repliesCount ??
      post.reply_count ??
      (Array.isArray(post.replies) ? post.replies.length : 0) ??
      0,

    // 🔁 Reposts
    repostsCount:
      post.repostsCount ??
      post.repost_count ??
      (Array.isArray(post.reposts) ? post.reposts.length : 0) ??
      0,
  };

  // ⭐ Normalize media URL to work with both relative + absolute URLs
  const mediaUrl = normalizedPost.media_url
    ? normalizedPost.media_url.startsWith("http")
      ? normalizedPost.media_url
      : `${process.env.REACT_APP_API_URL}${normalizedPost.media_url}`
    : null;

  // ✅ Treat URLs with query strings like .mp4?123 as video too
  const isVideo =
    !!mediaUrl && /\.(mp4|webm|ogg)(\?.*)?$/i.test(mediaUrl);

  const handleOverlayClick = (e) => {
    if (e.target.className === "modal-overlay") {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content">
        <button className="modal-close-btn" onClick={onClose}>
          ✖
        </button>

        <div className="modal-post">
          {/* 🧾 HEADER */}
          <div className="modal-post-header">
            <img
              src={
                normalizedPost.thumbnail || "/images/default-thumbnail.jpg"
              }
              alt={normalizedPost.username}
              className="modal-thumbnail"
              onClick={() => onNavigateToProfile(normalizedPost.username)}
            />
            <div className="modal-header-info">
              <span
                className="modal-username"
                onClick={() => onNavigateToProfile(normalizedPost.username)}
              >
                @{normalizedPost.username}
              </span>
              <span className="modal-date">
                {normalizedPost.createdAt
                  ? new Date(normalizedPost.createdAt).toLocaleString()
                  : ""}
              </span>
            </div>
          </div>

          {/* 📄 BODY */}
          <div className="modal-post-body">
            <p className="modal-post-text">
              {renderLinkedContent(normalizedPost.content)}
            </p>

            {normalizedPost.gif && (
              <img
                src={normalizedPost.gif}
                alt="GIF"
                className="modal-post-gif"
              />
            )}

            {/* 🎥 MEDIA / GIF / YOUTUBE */}
{(() => {
  const youtubeMatch = normalizedPost.content?.match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );

  const youtubeId = youtubeMatch ? youtubeMatch[1] : null;

  const gifUrl =
    normalizedPost.gif_url ||
    normalizedPost.gif ||
    null;

  if (youtubeId) {
    return (
      <div className="youtube-embed" onClick={(e) => e.stopPropagation()}>
        <iframe
          src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&mute=1&playsinline=1`}
          title="YouTube video player"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          style={{
            width: "100%",
            aspectRatio: "16 / 9",
            border: "none",
            borderRadius: "12px",
            marginTop: "12px",
          }}
        />
      </div>
    );
  }

  return (
    <>
      {mediaUrl &&
        (isVideo ? (
          <video
            key={mediaUrl}
            className="modal-post-media"
            autoPlay
            muted
            loop
            playsInline
            controls
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxHeight: "70vh",
              borderRadius: "12px",
            }}
          >
            <source src={mediaUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        ) : (
          <img
            src={mediaUrl}
            alt="Uploaded"
            className="modal-post-media"
            onClick={(e) => e.stopPropagation()}
          />
        ))}

      {gifUrl && (
        <img
          src={gifUrl}
          alt="GIF"
          className="modal-post-gif"
          onClick={(e) => e.stopPropagation()}
        />
      )}
    </>
  );
})()}
          </div>

          {/* 🧮 ACTION BAR WITH NORMALIZED COUNTS */}
          <div className="modal-post-actions">
            {/* ❤ LIKE */}
            <button
              title="Like"
              onClick={() => onLike && onLike(normalizedPost.id)}
            >
              ❤ {normalizedPost.likes}
            </button>

            {/* 💬 REPLY */}
            <button
              title="Reply"
              onClick={() => {
                onClose();
                setTimeout(() => {
                  if (onReply) onReply(normalizedPost);
                }, 50);
              }}
            >
              💬 {normalizedPost.repliesCount}
            </button>

            {/* 🔁 REPOST */}
            <button
              title="Repost"
              onClick={() => onRepost && onRepost(normalizedPost.id)}
            >
              🔁 {normalizedPost.repostsCount}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PostDetailModal;
